import EditDocumentModal from '@/components/modals/EditDocumentModal'; // Import the modal
import {
  filterFoldersForMove,
  getDocumentDownloadName,
  getDocumentFilenameForBlobHandling,
  getErrorMessage,
  getErrorRecord,
  getExplicitSelectedIds,
  getHistoricalTaxStatementId,
  HISTORICAL_TAX_VAULT_FILE2_ID,
  isPersonalStorageDocument2,
  isPreviewableExtension,
  toHistoricalTaxExternalFiles,
} from './documentVaultGridUtils';
import {
  DocumentActionErrorSnackbar,
  DocumentFileIcon,
  DocumentSelectionBar,
} from './DocumentListActions';
import CustomDataTable from '@/components/table/customDataTable'; // Assuming this is the styled wrapper
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { mobileWidth } from '@/constants';
import { isAdvisorUserRole } from '@/constants/roles';
import {
  Document2Record,
  DocumentFolderRecord,
  DocumentRecord,
  useBulkDownloadAdvisorDocumentsMutation,
  useBulkDownloadDocuments2Mutation,
  useBulkDownloadDocumentsMutation,
  useDeleteDocumentMutation,
  useDeleteFolderMutation,
  useDownloadDocumentMutation,
  useGetDocumentFoldersQuery,
  useUpdateDocumentMutation,
  usePreviewAdvisorInvestorDocumentMutation,
  usePreviewDocumentMutation,
} from '@/store/documentsApi';
import { WARootState } from '@/store/store';
import { isEmbeddableWidgetBundle } from '@/integrations/embeddableWidgetPortalUtils';
import { EmbeddableMenu } from '@/integrations/EmbeddableMenu';
import { WIDGET_GRID_HEIGHT } from '@/constants/grid';
import { Capacitor } from '@capacitor/core';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFileOutlined'; // Default file icon
import MoreVertIcon from '@mui/icons-material/MoreVert'; // Add MoreVertIcon
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Snackbar,
  type SvgIconProps,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  DataGridPremiumProps,
  GridColDef,
  type GridListViewColDef,
  GridRenderCellParams,
  GridEventLookup,
  GridPaginationModel,
  GridRowId,
  GridRowSelectionModel,
  gridPaginatedVisibleSortedGridRowIdsSelector,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import dayjs from '@/config/dayjs';
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { APP_DATE_FORMAT, formatAppDate } from '@/utils/date';
import { useLocalization } from '@/hooks/useLocalization';
import { useSelector } from 'react-redux';
import { DownloadOverlay } from '@/components/DownloadOverlay';
import { actionColumnConfig } from '@/components/grids/gridColumnConfigs';
import { GridContainer } from '@/components/grids/GridContainer';
import { VaultPaginationFooter } from './VaultPaginationFooter';
import { VAULT_PAGE_SIZE } from './vaultPagination';

// --- Type Definitions ---
// Define the union type for document records
export type AnyDocumentRecord = DocumentRecord | Document2Record;

/**
 * Widget-only grid overrides.
 *
 * The pixel height is what makes the vault scroll instead of growing without
 * bound in a host page. Each caller passes `height: '100%'`, which only
 * resolves if every ancestor up to the widget mount has a definite height —
 * they do not, so the grid renders at its natural height and a cap on the tile
 * just clips it. Pinning the grid itself is the same thing the Holdings and
 * Accounts widget grids do, and it leaves the vault header and footer alone.
 *
 * Embedded rows are also not clickable, so the hover highlight would advertise
 * an interaction that no longer exists.
 */
const EMBEDDED_GRID_SX: SxProps<Theme> = isEmbeddableWidgetBundle
  ? {
      height: WIDGET_GRID_HEIGHT,
      minHeight: WIDGET_GRID_HEIGHT,
      maxHeight: WIDGET_GRID_HEIGHT,
      '& .MuiDataGrid-row:not(.Mui-selected):hover': {
        backgroundColor: 'transparent',
      },
    }
  : {};

interface FolderOption {
  id: number | null;
  name: string;
  level: number;
}

const flattenFolders = (
  folders: DocumentFolderRecord[],
  path = '',
  level = 1,
): FolderOption[] => {
  let options: FolderOption[] = [];
  folders.forEach((folder) => {
    const currentPath = path ? `${path} / ${folder.Name}` : folder.Name;
    options.push({
      id: folder.VaultFileId ?? null,
      name: currentPath,
      level: folder.Level,
    });
    if (folder.Children.length > 0) {
      options = options.concat(
        flattenFolders(folder.Children, currentPath, level + 1),
      );
    }
  });
  return options;
};

// --- Component Props ---
interface DocVaultGridProps<
  T extends AnyDocumentRecord = AnyDocumentRecord,
> extends Omit<DataGridPremiumProps<T>, 'rows' | 'columns' | 'getRowId'> {
  rows: T[];
  columns: GridColDef<T>[];
  onRowAction?: (
    action: 'preview' | 'download' | 'folderClick',
    data: T,
  ) => void | Promise<void>;
  investorId?: number;
  currentFolderId?: number;
  bulkDownloadContextName?: string;
  showBulkActions?: boolean;
  /**
   * Controls the per-row Actions column (three-dot menu) and the equivalent
   * list-view kebab button. Defaults to `true`; the Recently Added (overview)
   * tab passes `false` because those actions have no valid end-user use there
   * (EUD-669).
   */
  showRowActions?: boolean;
  /**
   * Opens the row menu to advisors: Edit needs `CanRename`, plus `CanMove` on a
   * file (a folder that can't move, like a firm default one, is only renamed),
   * Delete is not offered, and a row without actions renders no button.
   * Defaults to `false`, which keeps the disabled demo menu for advisors.
   */
  enableAdvisorRowActions?: boolean;
  useDocuments2BulkDownload?: boolean;
  enableXsListView?: boolean;
  /**
   * List view only. Returns the date shown on each row, labelled with
   * `listViewDateLabel`. Without it the row shows its upload date as
   * "Last Updated".
   */
  getListViewDate?: (row: T) => Date | null;
  listViewDateLabel?: string;
  /**
   * Shows 25 rows per page with the vault footer. Defaults to `false`, which
   * keeps the single scrolling list without a footer.
   */
  paginated?: boolean;
  /**
   * Paginated grids return to the first page when this key changes. A refetch
   * that keeps the key keeps the page.
   */
  paginationResetKey?: string;
}

interface ListViewSelectAllCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  label: string;
}

const ListViewSelectAllCheckbox = ({
  checked,
  indeterminate,
  onChange,
  label,
}: ListViewSelectAllCheckboxProps) => (
  <Checkbox
    size='small'
    checked={checked}
    indeterminate={indeterminate}
    onChange={onChange}
    aria-label={label}
    sx={{ p: 0.5 }}
  />
);

const FIRST_VAULT_PAGE: GridPaginationModel = {
  page: 0,
  pageSize: VAULT_PAGE_SIZE,
};

const PAGE_IDS_EVENTS: Array<keyof GridEventLookup> = [
  'paginationModelChange',
  'filteredRowsSet',
  'sortedRowsSet',
  'rowsSet',
];

const sameIds = (a: GridRowId[], b: GridRowId[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

const modalHeaderStyle = {
  p: 2,
  borderBottom: 1,
  borderColor: 'divider',
};

// --- Helper Functions for Type Guards ---
const isDocumentRecord = (data: AnyDocumentRecord): data is DocumentRecord => {
  return 'VaultFileId' in data && 'IsFolder' in data;
};

const isDocument2Record = (
  data: AnyDocumentRecord,
): data is Document2Record => {
  return 'VaultFile2Id' in data; // Document2Record has VaultFile2Id
};

const listViewSecondaryTextSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontWeight: '400 !important',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const isFolder = (data: AnyDocumentRecord): boolean => {
  if (isDocumentRecord(data)) {
    return data.IsFolder;
  }
  // Document2Record needs logic to determine if it's a folder
  // Assuming lack of Extension implies folder for Document2Record
  if (isDocument2Record(data)) {
    return !data.Extension;
  }
  return false; // Default case
};

const getDocumentId = (data: AnyDocumentRecord): string | number => data.id;

const getDocumentName = (data: AnyDocumentRecord): string => {
  return data.Name || '';
};

const getDocumentExtension = (data: AnyDocumentRecord) =>
  isDocumentRecord(data) || isDocument2Record(data)
    ? data.Extension
    : undefined;

const getDocumentIcon = (
  data: AnyDocumentRecord,
  iconProps: SvgIconProps = { fontSize: 'small' },
) => (
  <DocumentFileIcon
    extension={getDocumentExtension(data)}
    isFolder={isFolder(data)}
    {...iconProps}
  />
);

const getDocumentVaultId = (data: AnyDocumentRecord): string | number => {
  if (isDocumentRecord(data)) {
    return data.VaultFileId;
  }
  if (isDocument2Record(data)) {
    return data.VaultFile2Id;
  }
  // Fallback, though should ideally hit one of the above
  // Depending on how 'id' is generated, it might be VaultFileId or VaultFile2Id
  // Let's return the number id as a safer fallback if types are correct
  return (data as { id: number }).id;
};

const getStatementId = (data: AnyDocumentRecord): string | undefined =>
  isDocument2Record(data) ? getHistoricalTaxStatementId(data) : undefined;

const getStatementName = (data: AnyDocumentRecord): string | undefined =>
  getStatementId(data) ? getDocumentDownloadName(data) : undefined;

const isAbortError = (error: unknown): boolean => {
  const record = getErrorRecord(error);
  return (
    record.status === 'FETCH_ERROR' &&
    typeof record.error === 'string' &&
    record.error.includes('AbortError')
  );
};

// --- DocVaultGrid Component ---
export const DocVaultGrid = <T extends AnyDocumentRecord>({
  rows,
  columns: propColumns,
  loading,
  onRowAction,
  investorId,
  currentFolderId,
  bulkDownloadContextName,
  showBulkActions = true,
  showRowActions = true,
  enableAdvisorRowActions = false,
  useDocuments2BulkDownload = false,
  enableXsListView = false,
  getListViewDate,
  listViewDateLabel,
  paginated = false,
  paginationResetKey,
  checkboxSelection = true,
  sx: sxProp,
  slots: slotsProp,
  apiRef: apiRefProp,
  onPaginationModelChange,
  ...rest
}: DocVaultGridProps<T>) => {
  const { t } = useLocalization();
  const isXs = useMediaQuery(mobileWidth);
  const isListView = enableXsListView && isXs;
  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProp ?? internalApiRef;
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set(),
    }); // Add state for row selection model
  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(FIRST_VAULT_PAGE);
  const [appliedResetKey, setAppliedResetKey] = useState(paginationResetKey);
  if (appliedResetKey !== paginationResetKey) {
    setAppliedResetKey(paginationResetKey);
    setPaginationModel(FIRST_VAULT_PAGE);
    setRowSelectionModel({ type: 'include', ids: new Set() });
  }
  const [visiblePageIds, setVisiblePageIds] = useState<GridRowId[]>([]);
  useEffect(() => {
    if (!paginated) return undefined;
    const readPageIds = () => {
      if (!apiRef.current) return;
      const ids = gridPaginatedVisibleSortedGridRowIdsSelector(apiRef);
      setVisiblePageIds((current) => (sameIds(current, ids) ? current : ids));
    };
    readPageIds();
    const grid = apiRef.current;
    if (!grid) return undefined;
    const unsubscribes = PAGE_IDS_EVENTS.map((eventName) =>
      grid.subscribeEvent(eventName, readPageIds),
    );
    return () => {
      unsubscribes.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [apiRef, paginated]);
  const [downloadingRows, setDownloadingRows] = useState<Set<string | number>>(
    new Set(),
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [actionFailure, setActionFailure] = useState<{
    kind: 'preview' | 'download';
    rows: T[];
  } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<
    string | null
  >(null);
  const [bulkDownloadSuccessMessage, setBulkDownloadSuccessMessage] = useState<
    string | null
  >(null); // State for bulk success
  const [bulkDownloadErrorMessage, setBulkDownloadErrorMessage] = useState<
    string | null
  >(null); // State for bulk error
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<T | null>(null); // State for file to delete

  const [bulkDeleteSuccessMessage, setBulkDeleteSuccessMessage] = useState<
    string | null
  >(null);
  const [bulkDeleteErrorMessage, setBulkDeleteErrorMessage] = useState<
    string | null
  >(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [bulkMoveSuccessMessage, setBulkMoveSuccessMessage] = useState<
    string | null
  >(null);
  const [bulkMoveErrorMessage, setBulkMoveErrorMessage] = useState<
    string | null
  >(null);
  const [isBulkMoving, setIsBulkMoving] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [selectedMoveFolder, setSelectedMoveFolder] =
    useState<FolderOption | null>(null);
  const [isFolderSelectorOpen, setIsFolderSelectorOpen] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayText, setOverlayText] = useState(t('downloading'));
  const abortControllers = useRef<Map<string | number, AbortController>>(
    new Map(),
  );
  const bulkDownloadAbortController = useRef<AbortController | null>(null);
  const isCapacitorApp = Capacitor.getPlatform() !== 'web';

  useEffect(() => {
    if (downloadingRows.size > 0) {
      setOverlayText(t('downloading'));
      setOverlayVisible(true);
    } else {
      setOverlayVisible(false);
    }
  }, [downloadingRows, t]);

  // State for actions menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | number | null>(null);

  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );

  // Use the download mutation hook
  const [triggerDownload] = useDownloadDocumentMutation();
  const [triggerBulkDownload] = useBulkDownloadDocumentsMutation();
  const [triggerBulkDownloadDocuments2] = useBulkDownloadDocuments2Mutation();
  const [triggerBulkDownloadAdvisor] =
    useBulkDownloadAdvisorDocumentsMutation();
  const [previewInvestorDoc] = usePreviewDocumentMutation();
  const [previewAdvisorDoc] = usePreviewAdvisorInvestorDocumentMutation();

  // Use the delete mutation hook
  const [triggerDelete, { isLoading: isDeleting }] =
    useDeleteDocumentMutation();

  // Use the new delete folder mutation hook
  const [triggerDeleteFolder, { isLoading: isDeletingFolder }] =
    useDeleteFolderMutation();

  const [updateDocument] = useUpdateDocumentMutation();

  // Fetch folders for move functionality
  const isAdvisorRole = isAdvisorUserRole(currentRole);
  const folderQueryArgs = isAdvisorRole
    ? typeof investorId === 'number'
      ? { InvestorId: investorId }
      : undefined
    : undefined;
  const { data: foldersData } = useGetDocumentFoldersQuery(folderQueryArgs, {
    // Investor mode calls /api/investor/DocumentFolders with no args.
    // Advisor mode requires an explicit investor id.
    skip: isAdvisorRole && typeof investorId !== 'number',
  });

  const selectedIds = useMemo(
    () => getExplicitSelectedIds(rowSelectionModel, rows, getDocumentId),
    [rowSelectionModel, rows],
  );

  const selectedRows = useMemo(
    () =>
      selectedIds
        .map((id) => rows.find((r) => getDocumentId(r) === id))
        .filter((r): r is T => !!r),
    [selectedIds, rows],
  );

  const selectedCount = selectedRows.length;
  const isAdvisor = isAdvisorUserRole(currentRole);
  // Folders stay selectable only where the bar can move or delete them.
  const canSelectFolders = showBulkActions && !isAdvisor;
  const isRowSelectable = useCallback(
    (row: T) => canSelectFolders || !isFolder(row),
    [canSelectFolders],
  );
  const downloadableSelectedRows = useMemo(
    () => selectedRows.filter((row) => !isFolder(row)),
    [selectedRows],
  );
  const downloadableCount = downloadableSelectedRows.length;

  const clearSelection = useCallback(() => {
    setRowSelectionModel({ type: 'include', ids: new Set() });
  }, []);

  useEffect(() => {
    clearSelection();
  }, [clearSelection, currentFolderId, investorId]);

  const handlePaginationModelChange = useCallback<
    NonNullable<DataGridPremiumProps<T>['onPaginationModelChange']>
  >(
    (model, details) => {
      if (paginated) setPaginationModel(model);
      clearSelection();
      onPaginationModelChange?.(model, details);
    },
    [clearSelection, onPaginationModelChange, paginated],
  );
  const editableSelections = useMemo(() => {
    return selectedRows.filter(
      (row): row is T & DocumentRecord =>
        isDocumentRecord(row) && row.CanRename,
    );
  }, [selectedRows]);
  const editableCount = editableSelections.length;
  const movableCount = useMemo(
    () =>
      selectedRows.filter((row) => isDocumentRecord(row) && row.CanMove).length,
    [selectedRows],
  );
  const deletableCount = useMemo(
    () =>
      selectedRows.filter((row) => isDocumentRecord(row) && row.CanDelete)
        .length,
    [selectedRows],
  );

  // Memoize folder options for move modal
  const folderOptions = useMemo(() => {
    const rootOption: FolderOption = {
      id: null,
      name: t('documentVault.title', 'Document Vault'),
      level: 0,
    };
    if (!foldersData?.Records) return [rootOption];

    const blockedFolderIds = new Set(
      selectedRows
        .filter((row) => isFolder(row))
        .map((row) => Number(getDocumentVaultId(row)))
        .filter((id) => !Number.isNaN(id)),
    );
    const availableFolders =
      blockedFolderIds.size > 0
        ? filterFoldersForMove(foldersData.Records, blockedFolderIds)
        : foldersData.Records;

    return [rootOption, ...flattenFolders(availableFolders)];
  }, [foldersData, selectedRows, t]);

  useEffect(() => {
    if (!isMoveModalOpen) return;

    setSelectedMoveFolder(
      folderOptions.find((option) => option.id === currentFolderId) ??
        folderOptions.find((option) => option.id === null) ??
        null,
    );
  }, [currentFolderId, folderOptions, isMoveModalOpen]);

  const handleSelectionModelChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      setRowSelectionModel(newSelectionModel);
    },
    [],
  );

  const selectedIdSet = useMemo(
    () => new Set(selectedIds.map((id) => String(id))),
    [selectedIds],
  );

  const toggleListRowSelection = useCallback(
    (id: string | number) => {
      setRowSelectionModel((current) => {
        const explicit = getExplicitSelectedIds(current, rows, getDocumentId);
        const key = String(id);
        const wasSelected = explicit.some(
          (existing) => String(existing) === key,
        );
        const nextIds = new Set(
          wasSelected
            ? explicit.filter((existing) => String(existing) !== key)
            : [...explicit, id],
        );
        return { type: 'include', ids: nextIds };
      });
    },
    [rows],
  );

  const selectAllScopeIds = useMemo<Array<string | number>>(() => {
    if (!paginated) {
      return rows.filter(isRowSelectable).map((row) => getDocumentId(row));
    }
    const rowsById = new Map(
      rows.map((row) => [String(getDocumentId(row)), row]),
    );
    return visiblePageIds.filter((id) => {
      const row = rowsById.get(String(id));
      return row !== undefined && isRowSelectable(row);
    });
  }, [paginated, visiblePageIds, rows, isRowSelectable]);
  const selectedInScopeCount = useMemo(
    () =>
      paginated
        ? selectAllScopeIds.filter((id) => selectedIdSet.has(String(id))).length
        : selectedCount,
    [paginated, selectAllScopeIds, selectedIdSet, selectedCount],
  );

  const toggleSelectAllRows = useCallback(() => {
    setRowSelectionModel((current) => {
      const explicit = getExplicitSelectedIds(current, rows, getDocumentId);
      if (!paginated) {
        const selectableRows = rows.filter(isRowSelectable);
        if (
          selectableRows.length > 0 &&
          explicit.length === selectableRows.length
        ) {
          return { type: 'include', ids: new Set() };
        }
        return {
          type: 'include',
          ids: new Set(selectableRows.map((row) => getDocumentId(row))),
        };
      }
      const scopeKeys = new Set(selectAllScopeIds.map((id) => String(id)));
      const allInScopeSelected =
        selectAllScopeIds.length > 0 &&
        selectAllScopeIds.every((id) =>
          explicit.some((selected) => String(selected) === String(id)),
        );
      if (allInScopeSelected) {
        return {
          type: 'include',
          ids: new Set(
            explicit.filter((selected) => !scopeKeys.has(String(selected))),
          ),
        };
      }
      return {
        type: 'include',
        ids: new Set([...explicit, ...selectAllScopeIds]),
      };
    });
  }, [rows, paginated, selectAllScopeIds, isRowSelectable]);

  const canPreview = useCallback(
    (data: T): boolean =>
      !isFolder(data) && isPreviewableExtension(getDocumentExtension(data)),
    [],
  );

  /**
   * Whether the row's Actions menu offers Preview. The embeddable widget bundle
   * omits it; row-click preview is unaffected.
   */
  const canShowPreviewAction = useCallback(
    (data: T): boolean => !isEmbeddableWidgetBundle && canPreview(data),
    [canPreview],
  );

  // No preview modal state needed; fetch blob directly for preview

  const advisorRowActionsEnabled =
    enableAdvisorRowActions && isAdvisorUserRole(currentRole);

  /** Whether the row offers at least one entry in its Actions menu. */
  const rowHasActions = useCallback(
    (row: T): boolean =>
      (!isCapacitorApp && canShowPreviewAction(row)) || // Preview Action (hidden in Capacitor app)
      (!isCapacitorApp && !isFolder(row)) || // Download Action (hidden in Capacitor app, files only)
      (isDocumentRecord(row) &&
        (advisorRowActionsEnabled
          ? row.CanRename && (row.CanMove || row.IsFolder)
          : row.CanRename)) || // Edit Action
      (!advisorRowActionsEnabled && isDocumentRecord(row) && row.CanDelete), // Delete Action
    [isCapacitorApp, canShowPreviewAction, advisorRowActionsEnabled],
  );

  const runAction = useCallback(
    async <R,>(
      kind: 'preview' | 'download',
      failedRows: T[],
      operation: () => Promise<R> | R,
      onCanceled?: () => void,
    ): Promise<boolean> => {
      setActionFailure(null);
      try {
        await operation();
        return true;
      } catch (err: unknown) {
        if (isAbortError(err)) {
          onCanceled?.();
        } else {
          console.error(`Document ${kind} failed:`, err);
          setActionFailure({ kind, rows: failedRows });
        }
        return false;
      }
    },
    [],
  );

  const runDelegatedAction = useCallback(
    async (kind: 'preview' | 'download', data: T): Promise<boolean> => {
      if (!onRowAction) return false;
      setOverlayText(t(kind === 'preview' ? 'previewing' : 'downloading'));
      setOverlayVisible(true);
      try {
        return await runAction(kind, [data], async () =>
          onRowAction(kind, data),
        );
      } finally {
        setOverlayVisible(false);
      }
    },
    [onRowAction, runAction, t],
  );

  const handleDownload = useCallback(
    async (data: T | null): Promise<boolean> => {
      if (!data || isFolder(data)) return false;

      const isPersonalStorageRow =
        isDocument2Record(data) && isPersonalStorageDocument2(data);
      if (onRowAction && (enableAdvisorRowActions || isPersonalStorageRow)) {
        return runDelegatedAction('download', data);
      }

      const vaultId = getDocumentVaultId(data);
      const rowId = getDocumentId(data);

      const filename = getDocumentDownloadName(data);

      if (downloadingRows.has(rowId)) {
        return false;
      }

      setDownloadError(null);
      setDownloadingRows((prev) => new Set(prev).add(rowId));

      const controller = new AbortController();
      abortControllers.current.set(rowId, controller);

      try {
        return await runAction(
          'download',
          [data],
          async () =>
            triggerDownload({
              vaultId,
              filename,
              statementId: getStatementId(data),
              statementName: getStatementName(data),
              InvestorId: investorId,
              signal: controller.signal,
            }).unwrap(),
          () => {
            setDownloadError(`Download canceled for "${filename}"`);
          },
        );
      } finally {
        abortControllers.current.delete(rowId);
        setDownloadingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });
      }
    },
    [
      triggerDownload,
      downloadingRows,
      onRowAction,
      enableAdvisorRowActions,
      investorId,
      runAction,
      runDelegatedAction,
    ],
  );

  const getZipName = useCallback(
    (rowsToZip: T[]) => {
      const contextName = (bulkDownloadContextName || 'document-vault').trim();
      const formattedDate = dayjs().format('YYYY-MM-DD');
      return `${contextName}-${String(rowsToZip.length)}-items-${formattedDate}.zip`;
    },
    [bulkDownloadContextName],
  );

  const downloadRows = useCallback(
    async (rowsToDownload: T[]) => {
      if (rowsToDownload.length === 0 || isBulkDownloading) return;

      if (rowsToDownload.length === 1) {
        const [row] = rowsToDownload;
        // A Personal Storage DocumentRecord downloads through the parent, which
        // knows its personal or account route.
        const downloaded =
          onRowAction && isDocumentRecord(row)
            ? await runDelegatedAction('download', row)
            : await handleDownload(row);
        if (downloaded) {
          clearSelection();
        }
        return;
      }

      setBulkDownloadErrorMessage(null);
      setBulkDownloadSuccessMessage(null);
      setIsBulkDownloading(true);
      setOverlayText(t('downloading'));
      setOverlayVisible(true);

      const controller = new AbortController();
      bulkDownloadAbortController.current = controller;
      const filename = getZipName(rowsToDownload);
      const vaultFileIds = (list: T[]) =>
        list.map((row) => String(getDocumentVaultId(row)));

      const downloaded = await runAction(
        'download',
        rowsToDownload,
        async () => {
          if (useDocuments2BulkDownload) {
            const isPersonalRow = (row: T) =>
              isDocument2Record(row) && isPersonalStorageDocument2(row);
            const isHistoricalTaxRow = (row: T) =>
              getDocumentVaultId(row) === HISTORICAL_TAX_VAULT_FILE2_ID;
            return triggerBulkDownloadDocuments2({
              vaultFileIds: vaultFileIds(
                rowsToDownload.filter(
                  (row) => !isPersonalRow(row) && !isHistoricalTaxRow(row),
                ),
              ),
              personalVaultFileIds: vaultFileIds(
                rowsToDownload.filter(isPersonalRow),
              ),
              externalFiles: toHistoricalTaxExternalFiles(
                rowsToDownload.flatMap((row) =>
                  isDocument2Record(row) ? [row] : [],
                ),
              ),
              investorId,
              filename,
              signal: controller.signal,
            }).unwrap();
          }
          if (isAdvisor && typeof investorId === 'number') {
            return triggerBulkDownloadAdvisor({
              investorId,
              vaultFileIds: vaultFileIds(rowsToDownload),
              filename,
              signal: controller.signal,
            }).unwrap();
          }
          return triggerBulkDownload({
            vaultFileIds: vaultFileIds(rowsToDownload),
            filename,
            signal: controller.signal,
          }).unwrap();
        },
        () => {
          setBulkDownloadErrorMessage(
            t('documentVault.bulkDownloadCanceled', 'Bulk download canceled.'),
          );
        },
      );

      bulkDownloadAbortController.current = null;
      setIsBulkDownloading(false);
      setOverlayVisible(false);

      if (downloaded) {
        clearSelection();
        setBulkDownloadSuccessMessage(
          t('documentVault.bulkDownloadSuccess', {
            defaultValue: 'Successfully downloaded "{{filename}}".',
            filename,
          }),
        );
      }
    },
    [
      clearSelection,
      getZipName,
      handleDownload,
      investorId,
      isAdvisor,
      isBulkDownloading,
      onRowAction,
      runAction,
      runDelegatedAction,
      t,
      triggerBulkDownload,
      triggerBulkDownloadAdvisor,
      triggerBulkDownloadDocuments2,
      useDocuments2BulkDownload,
    ],
  );

  const handleBulkDownload = useCallback(
    async () => downloadRows(downloadableSelectedRows),
    [downloadRows, downloadableSelectedRows],
  );

  // --- Delete Handlers ---
  const handleDeleteClick = useCallback((data: T) => {
    if (!isDocumentRecord(data) || !data.CanDelete) return; // Only allow delete for DocumentRecord with CanDelete=true
    setFileToDelete(data); // Set the file to be deleted
    setDeleteConfirmOpen(true); // Open confirmation modal
  }, []);

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null); // Clear the file to delete
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!fileToDelete) return; // Early exit if no file is selected

    const isFolderToDelete = isFolder(fileToDelete); // Check if it's a folder
    const vaultId = getDocumentVaultId(fileToDelete); // Get ID (works for both)
    const name =
      getDocumentName(fileToDelete) || (isFolderToDelete ? 'folder' : 'file');
    const deleteType = isFolderToDelete ? 'folder' : 'file'; // For messages

    console.log(
      `Confirming delete for ${deleteType} "${name}" (ID: ${String(vaultId)})`,
    );
    setDeleteError(null); // Clear previous errors
    setDeleteSuccessMessage(null);

    try {
      if (isFolderToDelete) {
        // Call the delete folder mutation
        console.log(`Calling triggerDeleteFolder for ID: ${String(vaultId)}`);
        await triggerDeleteFolder({ folderId: vaultId }).unwrap();
      } else {
        // Call the delete document mutation (only for DocumentRecord)
        if (isDocumentRecord(fileToDelete)) {
          console.log(`Calling triggerDelete for ID: ${String(vaultId)}`);
          await triggerDelete({ vaultId }).unwrap();
        } else {
          // Should not happen if delete is only enabled for DocumentRecord
          throw new Error('Cannot delete this item type');
        }
      }

      console.log(`Successfully deleted ${deleteType} "${name}"`);
      setDeleteSuccessMessage(`Successfully deleted ${deleteType} "${name}".`);
      handleCloseDeleteConfirm(); // Close modal on success
    } catch (err: unknown) {
      console.error(`Delete failed for ${deleteType} "${name}":`, err);
      const errorMessage = getErrorMessage(
        err,
        `Deletion of ${deleteType} failed`,
      );
      setDeleteError(
        `Failed to delete ${deleteType} "${name}": ${errorMessage}`,
      );
      // Optionally keep the modal open on error, or close it:
      // handleCloseDeleteConfirm();
    }
  }, [fileToDelete, triggerDelete, triggerDeleteFolder]); // Add triggerDeleteFolder dependency
  // --- End Delete Handlers ---

  // --- Menu Handlers ---
  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, rowId: string | number) => {
      setAnchorEl(event.currentTarget);
      setMenuRowId(rowId);
    },
    [],
  );

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const getRowById = useCallback(
    (id: string | number | null): T | null => {
      if (id === null) return null;
      return rows.find((row) => getDocumentId(row) === id) ?? null;
    },
    [rows],
  );

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const handleBulkDelete = useCallback(async () => {
    const selectedItems = selectedRows;
    if (selectedItems.length === 0) return;

    setBulkDeleteErrorMessage(null);
    setBulkDeleteSuccessMessage(null);
    if (isBulkDeleting) return;
    setIsBulkDeleting(true);

    try {
      await Promise.all(
        selectedItems.map(async (item) => {
          const vaultId = getDocumentVaultId(item);
          if (isFolder(item)) {
            await triggerDeleteFolder({ folderId: vaultId }).unwrap();
          } else if (isDocumentRecord(item)) {
            await triggerDelete({ vaultId }).unwrap();
          }
        }),
      );
      setBulkDeleteSuccessMessage(
        `Successfully deleted ${String(selectedItems.length)} item(s).`,
      );
      setRowSelectionModel({ type: 'include', ids: new Set() });
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, 'Bulk delete failed');
      setBulkDeleteErrorMessage(
        `Failed to delete selected items: ${errorMessage}`,
      );
    }
    setIsBulkDeleting(false);
  }, [selectedRows, triggerDelete, triggerDeleteFolder, isBulkDeleting]);

  const handleBulkMove = useCallback(async () => {
    if (!selectedMoveFolder) return;
    const itemsToMove = selectedRows.filter(
      (row): row is T & DocumentRecord => isDocumentRecord(row) && row.CanMove,
    );
    if (itemsToMove.length === 0) return;

    const parentId = selectedMoveFolder.id;

    setBulkMoveErrorMessage(null);
    setBulkMoveSuccessMessage(null);
    setIsBulkMoving(true);
    const investorIdParam =
      typeof investorId === 'number' ? investorId : undefined;

    try {
      await Promise.all(
        itemsToMove.map(async (doc) => {
          const payload = {
            VaultFileId: doc.VaultFileId,
            Name: doc.Name,
            ParentId: parentId,
            Permissions: doc.Permissions,
            Associations: doc.Associations,
            AccountId: doc.AccountId ?? null,
            InvestorId: investorIdParam,
            CanSign: doc.CanSign,
            IsSignatureRequested: doc.IsSignatureRequested,
            IsSigned: doc.IsSigned,
            AddedById: doc.AddedById,
            AddedByName: doc.AddedByName,
            CanDelete: doc.CanDelete,
            CanMove: doc.CanMove,
            CanRename: doc.CanRename,
            Extension: doc.Extension,
            FileSize: doc.FileSize,
            IsFolder: doc.IsFolder,
            LastReadDate: doc.LastReadDate,
            NumberOfFiles: doc.NumberOfFiles,
            NumberOfFolders: doc.NumberOfFolders,
            UploadDate: doc.UploadDate,
          } satisfies Parameters<typeof updateDocument>[0];

          await updateDocument(payload).unwrap();
        }),
      );
      setBulkMoveSuccessMessage(
        `Moved ${String(itemsToMove.length)} item(s) to ${selectedMoveFolder.name}.`,
      );
      setRowSelectionModel({ type: 'include', ids: new Set() });
      setIsMoveModalOpen(false);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, 'Bulk move failed');
      setBulkMoveErrorMessage(`Failed to move items: ${errorMessage}`);
    } finally {
      setIsBulkMoving(false);
    }
  }, [selectedRows, selectedMoveFolder, updateDocument, investorId]);

  const bulkDeleteSummary = useCallback((): string => {
    const selectedItems = selectedRows;

    const files = selectedItems.filter((item) => !isFolder(item));
    const folders = selectedItems.filter((item) => isFolder(item));
    const fileCount = files.length;
    const folderCount = folders.length;
    const docsInFolders = folders.reduce(
      (sum, f) => sum + (isDocumentRecord(f) ? f.NumberOfFiles || 0 : 0),
      0,
    );

    if (folderCount === 0) {
      return `${String(fileCount)} document${fileCount === 1 ? '' : 's'}`;
    }
    if (fileCount === 0) {
      if (folderCount === 1) {
        return `1 folder containing ${String(docsInFolders)} document${
          docsInFolders === 1 ? '' : 's'
        }`;
      }
      return `${String(folderCount)} folders containing ${String(docsInFolders)} documents total`;
    }
    return `${String(fileCount)} document${fileCount === 1 ? '' : 's'} and ${String(folderCount)} folder${folderCount === 1 ? '' : 's'} containing ${String(docsInFolders)} documents total`;
  }, [selectedRows]);

  const confirmBulkDelete = async () => {
    await handleBulkDelete();
    setBulkDeleteConfirmOpen(false);
  };

  const handleViewFile = useCallback(
    async (data: T) => {
      if (isFolder(data)) {
        await onRowAction?.('folderClick', data);
        return;
      }

      if (!canPreview(data)) {
        if (onRowAction) {
          await runDelegatedAction('download', data);
        } else {
          await handleDownload(data);
        }
        return;
      }

      const isAdvisorWithInvestor =
        isAdvisorUserRole(currentRole) && typeof investorId === 'number';
      // Personal Storage rows carry a VaultFiles id, so the parent routes them
      // to the personal storage endpoints.
      const isPersonalStorageRow =
        isDocument2Record(data) && isPersonalStorageDocument2(data);
      const shouldDelegatePreviewToParent =
        onRowAction &&
        (!(isAdvisorWithInvestor && isDocument2Record(data)) ||
          isPersonalStorageRow);

      if (shouldDelegatePreviewToParent) {
        await runDelegatedAction('preview', data);
        return;
      }

      setOverlayText(t('previewing'));
      setOverlayVisible(true);

      const fileName = getDocumentFilenameForBlobHandling(data);
      const vaultId = getDocumentVaultId(data);
      await runAction('preview', [data], async () =>
        isAdvisorWithInvestor && !isDocument2Record(data)
          ? previewAdvisorDoc({ investorId, vaultId, fileName }).unwrap()
          : previewInvestorDoc({
              vaultId,
              statementId: getStatementId(data),
              statementName: getStatementName(data),
              InvestorId: investorId,
              fileName,
            }).unwrap(),
      );

      setOverlayVisible(false);
    },
    [
      canPreview,
      onRowAction,
      currentRole,
      handleDownload,
      investorId,
      previewAdvisorDoc,
      previewInvestorDoc,
      runAction,
      runDelegatedAction,
      t,
    ],
  );

  const handleRetry = useCallback(() => {
    if (!actionFailure) return;
    const { kind, rows: failedRows } = actionFailure;
    setActionFailure(null);
    if (kind === 'preview') {
      void handleViewFile(failedRows[0]);
    } else {
      void downloadRows(failedRows);
    }
  }, [actionFailure, downloadRows, handleViewFile]);

  // --- Define Columns ---
  // Columns need to handle the generic type T
  const columns = useMemo<GridColDef<T>[]>(() => {
    // Add a file/folder icon column

    // Make the 'Name' column clickable
    const nameColumn = propColumns.find((col) => col.field === 'Name');
    const updatedNameColumn: GridColDef<T> | undefined = nameColumn
      ? {
          ...nameColumn,
          renderCell: (params: GridRenderCellParams<T>) => {
            const icon = getDocumentIcon(params.row, {
              sx: { color: 'text.secondary', mr: 1 },
            });

            const content = (
              <>
                {icon} {params.value ?? getDocumentName(params.row)}
              </>
            );

            if (isEmbeddableWidgetBundle && !isFolder(params.row)) {
              return (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                >
                  {content}
                </Box>
              );
            }

            const name = getDocumentName(params.row);
            const hint = isFolder(params.row)
              ? t('documentVault.clickToOpen', {
                  defaultValue: "Click to open '{{name}}'",
                  name,
                })
              : canPreview(params.row)
                ? t('documentVault.clickToPreview', {
                    defaultValue: "Click to preview '{{name}}'",
                    name,
                  })
                : t('documentVault.clickToDownload', {
                    defaultValue: "Click to download '{{name}}'",
                    name,
                  });

            return (
              <Tooltip
                title={hint}
                placement='bottom-start'
                describeChild
              >
                <Button
                  variant='text'
                  onClick={() => {
                    void handleViewFile(params.row);
                  }}
                  sx={{
                    p: 0,
                    m: 0,
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'inherit',
                    font: 'inherit',
                    letterSpacing: 'inherit',
                    textTransform: 'none',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {content}
                </Button>
              </Tooltip>
            );
          },
        }
      : undefined;

    // Add an actions column for explicit view/download/delete using a menu
    const actionsColumn: GridColDef<T> = {
      field: 'actions',
      type: 'actions',
      width: 80, // Reduced width as it's just an icon
      ...actionColumnConfig,
      renderCell: (params: GridRenderCellParams<T>) => {
        const row = params.row;
        const docId = getDocumentId(params.row);
        const isAdvisor = isAdvisorUserRole(currentRole);

        const rowHasAnyActions = rowHasActions(row);

        if (!rowHasAnyActions && advisorRowActionsEnabled) return null;

        // Keep kebab visible for layout consistency, but don't allow opening an empty menu
        if (!rowHasAnyActions) {
          return (
            <IconButton
              aria-label='actions'
              aria-controls={`actions-menu-${String(docId)}`}
              aria-haspopup='true'
              size='small'
              disabled
            >
              <MoreVertIcon />
            </IconButton>
          );
        }

        return isAdvisor && !advisorRowActionsEnabled ? (
          <Tooltip
            title={t(
              'demoFeatureUnavailable',
              "This feature isn't available in the demo. It's planned for a later release.",
            )}
            placement='bottom'
          >
            <span>
              <IconButton
                aria-label='actions'
                aria-controls={`actions-menu-${String(docId)}`}
                aria-haspopup='true'
                size='small'
                disabled
                sx={{ cursor: 'not-allowed', opacity: 0.6 }}
              >
                <MoreVertIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton
            aria-label='actions'
            aria-controls={`actions-menu-${String(docId)}`}
            aria-haspopup='true'
            onClick={(event) => {
              handleMenuOpen(event, docId);
            }}
            size='small'
          >
            <MoreVertIcon />
          </IconButton>
        );
      },
    };

    // List View Column

    // Combine columns: icon, modified name (if exists), others, actions
    let combinedColumns: GridColDef<T>[] = [];
    propColumns.forEach((col) => {
      if (col.field === 'Name' && updatedNameColumn) {
        combinedColumns.push(updatedNameColumn);
      } else if (col.field !== 'icon' && col.field !== 'actions') {
        // Ensure the passed column definition is compatible
        combinedColumns.push(col);
      }
    });

    const anyRowHasActions = rows.some(rowHasActions);

    // Only show the Actions column when enabled for this grid and at least one
    // row has actions available. The Recently Added (overview) tab disables it
    // via showRowActions (EUD-669).
    if (showRowActions && anyRowHasActions) {
      combinedColumns.push(actionsColumn);
    }

    // Add default valueFormatter for dates if not provided
    combinedColumns = combinedColumns.map((col) => {
      if (col.field === 'UploadDate' && !col.valueFormatter) {
        return {
          ...col,
          valueFormatter: (value: string) => {
            // Check if value is a string before passing to dayjs
            if (typeof value === 'string' && dayjs(value).isValid()) {
              return dayjs(value).format('MM/DD/YYYY');
            }
            return value;
          },
        };
      }
      return col;
    });

    return combinedColumns;
  }, [
    propColumns,
    rows,
    handleViewFile,
    canPreview,
    rowHasActions,
    advisorRowActionsEnabled,
    handleMenuOpen,
    currentRole,
    showRowActions,
    t,
  ]);

  // State for Edit Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentRecord | null>(null);
  const [selectedDocumentsForEdit, setSelectedDocumentsForEdit] = useState<
    DocumentRecord[]
  >([]);

  // Function to open the edit modal
  const handleEditClick = (document: DocumentRecord) => {
    setSelectedDocument(document);
    setSelectedDocumentsForEdit([document]);
    setEditModalOpen(true);
  };

  const handleBulkEditClick = () => {
    if (editableSelections.length === 0) return;
    setSelectedDocument(editableSelections[0] ?? null);
    setSelectedDocumentsForEdit(editableSelections);
    setEditModalOpen(true);
  };

  // Function to close the edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedDocument(null); // Clear selected document on close
    setSelectedDocumentsForEdit([]);
  };

  const listViewColDef: GridListViewColDef<T> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (params: GridRenderCellParams<T>) => {
        const row = params.row;
        const name = getDocumentName(row);
        const docId = getDocumentId(row);

        const uploadDate = getListViewDate
          ? formatAppDate(getListViewDate(row), {
              format: APP_DATE_FORMAT,
              fallback: '',
            })
          : 'UploadDate' in row
            ? formatAppDate(row.UploadDate, {
                format: APP_DATE_FORMAT,
                fallback: '',
              })
            : '';
        const dateLabel = getListViewDate
          ? (listViewDateLabel ?? '')
          : t('grid.lastUpdated', 'Last Updated');

        const uploaderName = (() => {
          if ('UploadedBy' in row && typeof row.UploadedBy === 'string') {
            const v = row.UploadedBy.trim();
            if (v) return v;
          }
          if ('AddedByName' in row && typeof row.AddedByName === 'string') {
            const v = row.AddedByName.trim();
            if (v) return v;
          }
          return '';
        })();

        const hasActions = rowHasActions(row);

        return (
          <Box
            onClick={
              isEmbeddableWidgetBundle && !isFolder(row)
                ? undefined
                : (event) => {
                    event.stopPropagation();
                    void handleViewFile(row);
                  }
            }
            sx={{
              width: '100%',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              minWidth: 0,
              cursor:
                isEmbeddableWidgetBundle && !isFolder(row)
                  ? 'default'
                  : 'pointer',
            }}
          >
            {checkboxSelection && (
              <Checkbox
                size='small'
                checked={selectedIdSet.has(String(docId))}
                disabled={!isRowSelectable(row)}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onChange={() => {
                  toggleListRowSelection(docId);
                }}
                aria-label={t('documentVault.selectRow', {
                  defaultValue: 'Select {{name}}',
                  name,
                })}
                sx={{ p: 0.25, mt: -0.25, flex: '0 0 auto' }}
              />
            )}

            <Box sx={{ pt: 0.25, flex: '0 0 auto' }}>
              {getDocumentIcon(row)}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant='body1'
                sx={{
                  fontWeight: '600 !important',
                  minWidth: 0,
                  // Allow full wrapping for long file names (no ellipsis).
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'unset',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {name}
              </Typography>

              {uploadDate && (
                <Typography
                  variant='body2'
                  sx={listViewSecondaryTextSx}
                >
                  {`${dateLabel} ${uploadDate}`}
                </Typography>
              )}
              {uploaderName && (
                <Typography
                  variant='body2'
                  sx={listViewSecondaryTextSx}
                >
                  {t('documentVault.uploadedBy', {
                    defaultValue: 'Uploaded by {{name}}',
                    name: uploaderName,
                  })}
                </Typography>
              )}
            </Box>

            {showRowActions && hasActions && (
              <IconButton
                size='small'
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuOpen(event, docId);
                }}
                aria-label={t('actions', 'Actions')}
              >
                <MoreVertIcon fontSize='small' />
              </IconButton>
            )}
          </Box>
        );
      },
    }),
    [
      rowHasActions,
      checkboxSelection,
      getListViewDate,
      listViewDateLabel,
      handleMenuOpen,
      handleViewFile,
      isRowSelectable,
      selectedIdSet,
      showRowActions,
      t,
      toggleListRowSelection,
    ],
  );

  const listViewSx: SxProps<Theme> = useMemo(
    () => ({
      '& .MuiDataGrid-virtualScrollerRenderZone': {
        width: '100% !important',
      },
      '& .MuiDataGrid-columnHeaders': { display: 'none' },
      '& .MuiDataGrid-cell': {
        px: 0,
        py: 0,
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      '& .MuiDataGrid-row': {
        width: '100% !important',
        minWidth: '100% !important',
        maxWidth: '100% !important',
      },
      '& .MuiDataGrid-cellContent': {
        width: '100%',
        minWidth: 0,
      },
    }),
    [],
  );

  const mergedSx: SxProps<Theme> = useMemo(() => {
    if (!isListView) {
      return [sxProp ?? {}, EMBEDDED_GRID_SX] as SxProps<Theme>;
    }
    return [sxProp ?? {}, listViewSx, EMBEDDED_GRID_SX] as SxProps<Theme>;
  }, [isListView, listViewSx, sxProp]);

  return (
    <>
      {!isCapacitorApp &&
        rows.length > 0 &&
        (selectedCount > 0 || isListView) && (
          <DocumentSelectionBar
            selectedCount={selectedCount}
            downloadCount={downloadableCount}
            isDownloading={isBulkDownloading}
            onDownload={() => {
              void handleBulkDownload();
            }}
            leading={
              isListView &&
              checkboxSelection && (
                <ListViewSelectAllCheckbox
                  checked={
                    selectAllScopeIds.length > 0 &&
                    selectedInScopeCount === selectAllScopeIds.length
                  }
                  indeterminate={
                    selectedInScopeCount > 0 &&
                    selectedInScopeCount < selectAllScopeIds.length
                  }
                  onChange={toggleSelectAllRows}
                  label={t('selectAll', 'Select All')}
                />
              )
            }
          >
            {showBulkActions && (
              <Tooltip
                title={
                  isAdvisor
                    ? t(
                        'demoFeatureUnavailable',
                        "This feature isn't available in the demo. It's planned for a later release.",
                      )
                    : editableCount > 0
                      ? ''
                      : t(
                          'documentVault.bulkEditSelectionHint',
                          'Select at least one editable item to use Edit.',
                        )
                }
                disableHoverListener={isAdvisor ? false : editableCount > 0}
              >
                <span>
                  <Button
                    fullWidth={isXs}
                    variant='outlined'
                    startIcon={<EditIcon />}
                    onClick={handleBulkEditClick}
                    disabled={isAdvisor || editableCount === 0}
                  >
                    {t('edit', 'Edit')} ({editableCount})
                  </Button>
                </span>
              </Tooltip>
            )}

            {showBulkActions && (
              <Tooltip
                title={
                  isAdvisor
                    ? t(
                        'demoFeatureUnavailable',
                        "This feature isn't available in the demo. It's planned for a later release.",
                      )
                    : movableCount === 0
                      ? t(
                          'documentVault.bulkMoveSelectionHint',
                          'Select at least one movable item to use Move.',
                        )
                      : ''
                }
                disableHoverListener={isAdvisor ? false : movableCount > 0}
              >
                <span>
                  <Button
                    fullWidth={isXs}
                    variant='outlined'
                    startIcon={<DriveFileMoveOutlinedIcon />}
                    onClick={() => {
                      setSelectedMoveFolder(
                        folderOptions.find(
                          (option) => option.id === currentFolderId,
                        ) ??
                          folderOptions.find((option) => option.id === null) ??
                          null,
                      );
                      setIsMoveModalOpen(true);
                    }}
                    disabled={isAdvisor || movableCount === 0}
                  >
                    {t('documentVault.moveToFolder', 'Move To Folder')} (
                    {movableCount})
                  </Button>
                </span>
              </Tooltip>
            )}

            {showBulkActions && (
              <Tooltip
                title={
                  isAdvisor
                    ? t(
                        'demoFeatureUnavailable',
                        "This feature isn't available in the demo. It's planned for a later release.",
                      )
                    : deletableCount === 0
                      ? t(
                          'documentVault.bulkDeleteSelectionHint',
                          'Select at least one deletable item to use Delete.',
                        )
                      : ''
                }
                disableHoverListener={isAdvisor ? false : deletableCount > 0}
              >
                <span>
                  <Button
                    fullWidth={isXs}
                    variant='outlined'
                    color='error'
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setBulkDeleteConfirmOpen(true);
                    }}
                    disabled={isAdvisor || deletableCount === 0}
                  >
                    {t('delete', 'Delete')} ({deletableCount})
                  </Button>
                </span>
              </Tooltip>
            )}
          </DocumentSelectionBar>
        )}

      <GridContainer isWidget={isEmbeddableWidgetBundle}>
        <CustomDataTable<T> // Pass generic type to CustomDataTable
          rows={rows}
          columns={columns}
          loading={loading} // Base loading prop from parent
          checkboxSelection={checkboxSelection && rows.length > 0}
          disableRowSelectionOnClick
          rowSelectionModel={rowSelectionModel} // Pass state to the grid
          onRowSelectionModelChange={handleSelectionModelChange} // Correct signature now
          isRowSelectable={(params) => isRowSelectable(params.row)}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={(row: T) => getDocumentId(row)} // Use helper
          rowHeight={52}
          showToolbar={false}
          disablePivoting
          {...(isListView ? { listView: true } : {})}
          {...(isListView ? { listViewColumn: listViewColDef } : {})}
          {...(isListView ? { getRowHeight: () => 'auto' as const } : {})}
          slots={{
            ...(isListView && !isCapacitorApp
              ? { toolbar: XsPreferencesDrawerToolbar }
              : {}),
            ...(paginated ? { footer: VaultPaginationFooter } : {}),
            ...slotsProp,
          }}
          sx={mergedSx}
          apiRef={apiRef}
          {...(paginated
            ? {
                pagination: true,
                paginationModel,
                pageSizeOptions: [VAULT_PAGE_SIZE],
                hideFooter: false,
                checkboxSelectionVisibleOnly: true,
              }
            : {})}
          {...rest}
        />
      </GridContainer>

      {/*
        Action Menu. In widgets this centres in the mount node over a scoped
        backdrop, matching the grid filter panels. That only reads well because
        the embedded vault gives its grid a fixed WIDGET_GRID_HEIGHT slot, so
        the centre of the container stays near the row the user clicked.
      */}
      <EmbeddableMenu
        id={`actions-menu-${String(menuRowId)}`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          list: {
            'aria-labelledby': 'actions',
          },
        }}
      >
        {(() => {
          const currentRow = getRowById(menuRowId);
          if (!currentRow) return null; // Should not happen based on renderCell logic, but safe check

          const menuItems = [];
          const docId = getDocumentId(currentRow);
          const isDownloading = downloadingRows.has(docId);
          const isCurrentDeleting =
            (isDeleting && // Check file delete loading
              !isFolder(currentRow) &&
              fileToDelete?.id === docId) ||
            (isDeletingFolder && // Check folder delete loading
              isFolder(currentRow) &&
              fileToDelete?.id === docId);

          // Preview Action (hidden in Capacitor app and in widget bundles)
          if (!isCapacitorApp && canShowPreviewAction(currentRow)) {
            menuItems.push(
              <MenuItem
                key='preview'
                onClick={() => {
                  handleMenuAction(() => {
                    void handleViewFile(currentRow);
                  });
                }}
              >
                <ListItemIcon>
                  <InsertDriveFileIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>{t('preview', 'Preview')}</ListItemText>
              </MenuItem>,
            );
          }

          // Download Action (hidden in Capacitor app)
          if (!isCapacitorApp && !isFolder(currentRow)) {
            // Only show download for files
            menuItems.push(
              <MenuItem
                key='download'
                onClick={() => {
                  handleMenuAction(() => {
                    void handleDownload(currentRow);
                  });
                }}
                disabled={isDownloading} // Correctly use isDownloading state
              >
                <ListItemIcon>
                  {isDownloading ? ( // Correctly use isDownloading state
                    <CircularProgress size={20} />
                  ) : (
                    <DownloadIcon fontSize='small' />
                  )}
                </ListItemIcon>
                <ListItemText>
                  {isDownloading
                    ? t('downloading', 'Downloading...')
                    : t('download', 'Download')}
                </ListItemText>
              </MenuItem>,
            );
          }

          // Edit Action
          if (
            isDocumentRecord(currentRow) &&
            (advisorRowActionsEnabled
              ? currentRow.CanRename &&
                (currentRow.CanMove || currentRow.IsFolder)
              : currentRow.CanRename)
          ) {
            const isAdvisor = isAdvisorUserRole(currentRole);
            if (isAdvisor && !advisorRowActionsEnabled) {
              menuItems.push(
                <Tooltip
                  key='edit-disabled'
                  title={t(
                    'demoFeatureUnavailable',
                    "This feature isn't available in the demo. It's planned for a later release.",
                  )}
                  placement='bottom'
                >
                  <span>
                    <MenuItem
                      disabled
                      sx={{ cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      <ListItemIcon>
                        <EditIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>{t('edit', 'Edit')}</ListItemText>
                    </MenuItem>
                  </span>
                </Tooltip>,
              );
            } else {
              menuItems.push(
                <MenuItem
                  key='edit'
                  onClick={() => {
                    handleMenuAction(() => {
                      handleEditClick(currentRow);
                    });
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>{t('edit', 'Edit')}</ListItemText>
                </MenuItem>,
              );
            }
          }

          // Delete Action
          if (
            !advisorRowActionsEnabled &&
            isDocumentRecord(currentRow) &&
            currentRow.CanDelete
          ) {
            const isAdvisor = isAdvisorUserRole(currentRole);
            if (isAdvisor) {
              menuItems.push(
                <Tooltip
                  key='delete-disabled'
                  title={t(
                    'demoFeatureUnavailable',
                    "This feature isn't available in the demo. It's planned for a later release.",
                  )}
                  placement='bottom'
                >
                  <span>
                    <MenuItem
                      disabled
                      sx={{ cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      <ListItemIcon>
                        <DeleteIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText sx={{ color: 'error.main' }}>
                        Delete
                      </ListItemText>
                    </MenuItem>
                  </span>
                </Tooltip>,
              );
            } else {
              menuItems.push(
                <MenuItem
                  key='delete'
                  onClick={() => {
                    handleMenuAction(() => {
                      handleDeleteClick(currentRow);
                    });
                  }}
                  disabled={isCurrentDeleting}
                >
                  <ListItemIcon>
                    {isCurrentDeleting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DeleteIcon fontSize='small' />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      color: isCurrentDeleting ? 'text.disabled' : 'error.main',
                    }}
                  >
                    {isCurrentDeleting ? 'Deleting...' : 'Delete'}
                  </ListItemText>
                </MenuItem>,
              );
            }
          }

          return menuItems;
        })()}
      </EmbeddableMenu>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        aria-labelledby='delete-confirm-title'
        aria-describedby='delete-confirm-description'
        fullScreen={isXs}
        slotProps={{ paper: { sx: isXs ? undefined : { width: 400 } } }}
      >
        <Box sx={modalHeaderStyle}>
          <Typography
            id='delete-confirm-title'
            variant='h6'
            component='h2'
          >
            Confirm Deletion
          </Typography>
        </Box>
        <Box
          sx={{ p: 2 }}
          id='delete-confirm-description'
        >
          <Typography>
            Are you sure you want to delete the{' '}
            {fileToDelete && isFolder(fileToDelete) ? 'folder' : 'file'} "
            {fileToDelete ? getDocumentName(fileToDelete) : ''}"?
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
          >
            This action cannot be undone.
          </Typography>
        </Box>
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: 1,
            borderColor: 'divider',
            gap: 1, // Add gap between buttons
          }}
        >
          <Button
            size='large'
            onClick={handleCloseDeleteConfirm}
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            size='large'
            onClick={() => {
              void handleConfirmDelete();
            }}
            variant='contained'
            color='error'
            disabled={isDeleting || isDeletingFolder} // Disable button if any delete is in progress
            startIcon={
              isDeleting || isDeletingFolder ? ( // Show spinner if any delete is in progress
                <CircularProgress
                  size={16}
                  color='inherit'
                />
              ) : null
            }
          >
            {isDeleting || isDeletingFolder ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog
        open={bulkDeleteConfirmOpen}
        onClose={() => {
          setBulkDeleteConfirmOpen(false);
        }}
        aria-labelledby='bulk-delete-confirm-title'
        fullScreen={isXs}
        slotProps={{ paper: { sx: isXs ? undefined : { width: 400 } } }}
      >
        <Box sx={modalHeaderStyle}>
          <Typography
            id='bulk-delete-confirm-title'
            variant='h6'
            component='h2'
          >
            Confirm Deletion
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography>
            You are about to delete{' '}
            <Box
              component='span'
              fontWeight='bold'
            >
              {bulkDeleteSummary()}
            </Box>{' '}
            from your vault. This cannot be undone.
          </Typography>
        </Box>
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: 1,
            borderColor: 'divider',
            gap: 1,
          }}
        >
          <Button
            size='large'
            onClick={() => {
              setBulkDeleteConfirmOpen(false);
            }}
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            size='large'
            onClick={() => {
              void confirmBulkDelete();
            }}
            variant='contained'
            color='error'
            disabled={isBulkDeleting}
            startIcon={
              isBulkDeleting ? (
                <CircularProgress
                  size={16}
                  color='inherit'
                />
              ) : null
            }
          >
            {isBulkDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Dialog>

      {/* Bulk Move Modal */}
      <Dialog
        open={isMoveModalOpen}
        onClose={() => {
          setIsMoveModalOpen(false);
        }}
        aria-labelledby='move-modal-title'
        fullScreen={isXs}
        slotProps={{ paper: { sx: isXs ? undefined : { width: 400 } } }}
      >
        <Box sx={modalHeaderStyle}>
          <Typography
            id='move-modal-title'
            variant='h6'
            component='h2'
          >
            Move Items
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography>
            Move {String(selectedCount)} selected item(s) to
          </Typography>
          <Box
            display='flex'
            alignItems='center'
            sx={{ mt: 1 }}
          >
            <Typography sx={{ mr: 1 }}>
              {selectedMoveFolder?.name ?? 'Document Vault'}
            </Typography>
            <Link
              component='button'
              type='button'
              variant='body2'
              onClick={() => {
                setIsFolderSelectorOpen(true);
              }}
            >
              Change Folder
            </Link>
          </Box>
          {isFolderSelectorOpen && (
            <Autocomplete
              open={isFolderSelectorOpen}
              onClose={() => {
                setIsFolderSelectorOpen(false);
              }}
              id='move-folder-autocomplete'
              options={folderOptions}
              noOptionsText={t('noFoldersAvailable', 'No folders available')}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => String(option.id ?? 'root')}
              value={selectedMoveFolder}
              onChange={(_event, newValue) => {
                setSelectedMoveFolder(newValue);
                setIsFolderSelectorOpen(false);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Select Folder'
                  variant='outlined'
                  autoFocus
                />
              )}
              renderOption={(props, option) => {
                const { key: _key, ...otherProps } = props;
                return (
                  <li
                    {...otherProps}
                    style={{
                      paddingLeft: `${String(option.level * 1.5)}em`,
                    }}
                  >
                    {option.name.split(' / ').pop()}
                  </li>
                );
              }}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: 1,
            borderColor: 'divider',
            gap: 1,
          }}
        >
          <Button
            onClick={() => {
              setIsMoveModalOpen(false);
            }}
            variant='outlined'
            size='large'
          >
            Cancel
          </Button>
          <Button
            size='large'
            onClick={() => {
              void handleBulkMove();
            }}
            variant='contained'
            disabled={isBulkMoving}
            startIcon={
              isBulkMoving ? (
                <CircularProgress
                  size={16}
                  color='inherit'
                />
              ) : null
            }
          >
            {isBulkMoving ? 'Moving...' : 'Move'}
          </Button>
        </Box>
      </Dialog>

      {/* Render the Edit Modal */}
      <EditDocumentModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        document={selectedDocument}
        documents={selectedDocumentsForEdit}
        investorId={investorId}
      />

      {/* Error/Success Snackbar */}
      <Snackbar
        open={
          !!downloadError ||
          !!deleteError ||
          !!deleteSuccessMessage ||
          !!bulkDownloadErrorMessage ||
          !!bulkDownloadSuccessMessage ||
          !!bulkDeleteErrorMessage ||
          !!bulkDeleteSuccessMessage ||
          !!bulkMoveErrorMessage ||
          !!bulkMoveSuccessMessage
        }
        autoHideDuration={6000} // Hide after 6 seconds
        onClose={() => {
          setDownloadError(null);
          setDeleteError(null);
          setDeleteSuccessMessage(null);
          setBulkDownloadErrorMessage(null); // Clear bulk error on close
          setBulkDownloadSuccessMessage(null); // Clear bulk success on close
          setBulkDeleteErrorMessage(null);
          setBulkDeleteSuccessMessage(null);
          setBulkMoveErrorMessage(null);
          setBulkMoveSuccessMessage(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            setDownloadError(null);
            setDeleteError(null);
            setDeleteSuccessMessage(null);
            setBulkDownloadErrorMessage(null); // Clear bulk error on close
            setBulkDownloadSuccessMessage(null); // Clear bulk success on close
            setBulkDeleteErrorMessage(null);
            setBulkDeleteSuccessMessage(null);
            setBulkMoveErrorMessage(null);
            setBulkMoveSuccessMessage(null);
          }}
          severity={
            deleteError ||
            downloadError ||
            bulkDownloadErrorMessage ||
            bulkDeleteErrorMessage ||
            bulkMoveErrorMessage
              ? 'error'
              : 'success'
          } // Determine severity including bulk
          variant='filled'
          sx={{ width: '100%' }}
        >
          {/* Show the appropriate message, prioritizing errors */}
          {deleteError ||
            downloadError ||
            bulkDownloadErrorMessage ||
            bulkDeleteErrorMessage ||
            bulkMoveErrorMessage ||
            deleteSuccessMessage ||
            bulkDownloadSuccessMessage ||
            bulkDeleteSuccessMessage ||
            bulkMoveSuccessMessage}
        </Alert>
      </Snackbar>
      <DocumentActionErrorSnackbar
        failedAction={actionFailure?.kind ?? null}
        onRetry={handleRetry}
        onClose={() => {
          setActionFailure(null);
        }}
      />
      <DownloadOverlay
        visible={overlayVisible}
        text={overlayText}
        onCancel={() => {
          abortControllers.current.forEach((controller) => {
            controller.abort();
          });
          abortControllers.current.clear();
          bulkDownloadAbortController.current?.abort();
          bulkDownloadAbortController.current = null;
          setDownloadingRows(new Set());
          setIsBulkDownloading(false);
        }}
      />
    </>
  );
};
